/////////////////////////////////////////////////////////
// Configurations related to listing.                  //
// Main configuration here is the extended data config //
/////////////////////////////////////////////////////////

// Note: The listingFields come from listingFields asset nowadays by default.
//       To use this built-in configuration, you need to change the overwrite from configHelper.js
//       (E.g. use mergeDefaultTypesAndFieldsForDebugging func)

/**
 * Configuration options for listing fields (custom extended data fields):
 * - key:                           Unique key for the extended data field.
 * - scope (optional):              Scope of the extended data can be either 'public' or 'private'.
 *                                  Default value: 'public'.
 *                                  Note: listing doesn't support 'protected' scope atm.
 * - schemaType (optional):         Schema for this extended data field.
 *                                  This is relevant when rendering components and querying listings.
 *                                  Possible values: 'enum', 'multi-enum', 'text', 'long', 'boolean'.
 * - enumOptions (optional):        Options shown for 'enum' and 'multi-enum' extended data.
 *                                  These are used to render options for inputs and filters on
 *                                  EditListingPage, ListingPage, and SearchPage.
 * - listingTypeConfig (optional):  Relationship configuration against listing types.
 *   - limitToListingTypeIds:         Indicator whether this listing field is relevant to a limited set of listing types.
 *   - listingTypeIds:                An array of listing types, for which this custom listing field is
 *                                    relevant and should be added. This is mandatory if limitToListingTypeIds is true.
 * - categoryConfig (optional):     Relationship configuration against categories.
 *   - limitToCategoryIds:            Indicator whether this listing field is relevant to a limited set of categories.
 *   - categoryIds:                   An array of categories, for which this custom listing field is
 *                                    relevant and should be added. This is mandatory if limitToCategoryIds is true.
 * - filterConfig:                  Filter configuration for listings query.
 *    - indexForSearch (optional):    If set as true, it is assumed that the extended data key has
 *                                    search index in place. I.e. the key can be used to filter
 *                                    listing queries (then scope needs to be 'public').
 *                                    Note: Flex CLI can be used to set search index for the key:
 *                                    https://www.sharetribe.com/docs/references/extended-data/#search-schema
 *                                    Read more about filtering listings with public data keys from API Reference:
 *                                    https://www.sharetribe.com/api-reference/marketplace.html#extended-data-filtering
 *                                    Default value: false,
 *   - filterType:                    Sometimes a single schemaType can be rendered with different filter components.
 *                                    For 'enum' schema, filterType can be 'SelectSingleFilter' or 'SelectMultipleFilter'
 *   - label:                         Label for the filter, if the field can be used as query filter
 *   - searchMode (optional):         Search mode for indexed data with multi-enum schema.
 *                                    Possible values: 'has_all' or 'has_any'.
 *   - group:                         SearchPageWithMap has grouped filters. Possible values: 'primary' or 'secondary'.
 * - showConfig:                    Configuration for rendering listing. (How the field should be shown.)
 *   - label:                         Label for the saved data.
 *   - isDetail                       Can be used to hide detail row (of type enum, boolean, or long) from listing page.
 *                                    Default value: true,
 * - saveConfig:                    Configuration for adding and modifying extended data fields.
 *   - label:                         Label for the input field.
 *   - placeholderMessage (optional): Default message for user input.
 *   - isRequired (optional):         Is the field required for providers to fill
 *   - requiredMessage (optional):    Message for those fields, which are mandatory.
 */

// Marketplace categories configuration (for Equipment, For Riding, etc.)
export const marketplaceCategories = [
  {
    key: "For The Horse",
    label: "For The Horse",
    childCategories: [
      { key: "grooming_supplies", label: "Grooming Supplies" },
      { key: "hoof_care", label: "Hoof Care" },
      { key: "horse_bathing", label: "Horse Bathing" },
      { key: "horse_care_nutrition", label: "Horse Care & Nutrition" },
      { key: "horse_wear", label: "Horse Wear" },
      { key: "show_halter_equipment", label: "Show Halter Equipment" },
      { key: "show_prep", label: "Show Prep" },
      { key: "tail_care", label: "Tail Care" },
      { key: "training_equipment", label: "Training Equipment" },
      { key: "vehicles_harnesses", label: "Vehicles & Harnesses" }
    ]
  },
  {
    key: "For The Barn",
    label: "For The Barn",
    childCategories: [
      { key: "barn_equipment", label: "Barn Equipment" },
      { key: "feeding", label: "Feeding" },
      { key: "fencing_pens", label: "Fencing & Pens" },
      { key: "jumps_equipment", label: "Jumps & Equipment" },
      { key: "organization_storage", label: "Organization & Storage" },
      { key: "stable_supplies", label: "Stable Supplies" }
    ]
  },
  {
    key: "For Riding",
    label: "For Riding",
    childCategories: [
      { key: "bits", label: "Bits" },
      { key: "bridles", label: "Bridles" },
      { key: "saddle_accessories", label: "Saddle Accessories" },
      { key: "saddle_pads", label: "Saddle Pads" },
      { key: "saddles", label: "Saddles" }
    ]
  },
  {
    key: "For The Rider",
    label: "For The Rider",
    childCategories: [
      { key: "english_show_attire", label: "English Show Attire" },
      { key: "equestrian_attire", label: "Equestrian Attire" },
      { key: "saddle_seat_show_attire", label: "Saddle Seat Show Attire" },
      { key: "western_attire", label: "Western Attire" }
    ]
  },
  {
    key: "Equipment",
    label: "Equipment",
    childCategories: [
      { key: "transportation_equipment", label: "Transportation Equipment" }
    ]
  },
  {
    key: "Services",
    label: "Services",
    childCategories: [
      { key: "services", label: "Services" }
    ]
  },
  {
    key: "Podcasts",
    label: "Podcasts",
    childCategories: [
      { key: "podcasts", label: "Podcasts" }
    ]
  },
  {
    key: "Other",
    label: "Other",
    childCategories: []
  }
];

export const businessCategories = [
  {
    key: "Boarding Services", label: "Boarding Services", childCategories: [
      { "key": "dry_stall_boarding", "label": "Dry Stall Boarding" },
      { "key": "full_service_boarding", "label": "Full Service Boarding" },
      { "key": "pasture_boarding", "label": "Pasture Boarding" },
      { "key": "self_care_boarding", "label": "Self Care Boarding" },
      { "key": "stall_boarding", "label": "Stall Boarding" },
      { "key": "other", "label": "Other" }
    ]
  },
  {
    key: "Breeding & Reproduction", label: "Breeding & Reproduction", childCategories: [
      { "key": "breeding_services", "label": "Breeding Services" },
      { "key": "foaling_services", "label": "Foaling Services" },
      { "key": "mare_care", "label": "Mare Care" },
      { "key": "stallion_standing", "label": "Stallion Standing" },
      { "key": "other", "label": "Other" }
    ]
  },
  {
    key: "Disciplines Served", label: "Disciplines Served", childCategories: [
      { "key": "all", "label": "All" },
      { "key": "cutting", "label": "Cutting" },
      { "key": "dressage", "label": "Dressage" },
      { "key": "driving", "label": "Driving" },
      { "key": "equitation", "label": "Equitation" },
      { "key": "eventing", "label": "Eventing" },
      { "key": "hunters", "label": "Hunters" },
      { "key": "jumpers", "label": "Jumpers" },
      { "key": "minis", "label": "Minis" },
      { "key": "polo", "label": "Polo" },
      { "key": "racing", "label": "Racing" },
      { "key": "ranch_riding", "label": "Ranch Riding" },
      { "key": "reining", "label": "Reining" },
      { "key": "saddleseat", "label": "Saddleseat" },
      { "key": "trail_riding", "label": "Trail Riding" },
      { "key": "western", "label": "Western" },
      { "key": "other", "label": "Other" }
    ]
  },
  {
    key: "Feed & Supplements", label: "Feed & Supplements", childCategories: [
      { "key": "feed_reseller", "label": "Feed Reseller" },
      { "key": "feed_store", "label": "Feed Store" },
      { "key": "hay_producer", "label": "Hay Producer" },
      { "key": "supplement_provider", "label": "Supplement Provider" },
      { "key": "other", "label": "Other" }
    ]
  },
  {
    key: "Horse Health & Wellness", label: "Horse Health & Wellness", childCategories: [
      { "key": "bemer_therapy", "label": "BEMER Therapy" },
      { "key": "magnawave_technician", "label": "Certified Magnawave Technician" },
      { "key": "dental_care", "label": "Dental Care" },
      { "key": "equine_acupuncturist", "label": "Equine Acupuncturist" },
      { "key": "equine_chiropractor", "label": "Equine Chiropractor" },
      { "key": "farrier", "label": "Farrier" },
      { "key": "massage_therapist", "label": "Massage Therapist" },
      { "key": "pemf_practitioner", "label": "PEMF Practitioner" },
      { "key": "sheath_cleaning", "label": "Sheath Cleaning" },
      { "key": "veterinarian", "label": "Veterinarian" },
      { "key": "other", "label": "Other" }
    ]
  },
  {
    key: "Non-Profit Organization", label: "Non-Profit Organization", childCategories: [
      { "key": "adoption_org", "label": "Adoption Organization" },
      { "key": "horse_show_association", "label": "Horse Show Association" },
      { "key": "sanctuary", "label": "Sanctuary" },
      { "key": "therapeutic_riding", "label": "Therapeutic Riding" },
      { "key": "trail_riding_club", "label": "Trail Riding Club" },
      { "key": "other", "label": "Other" }
    ]
  },
  {
    key: "Professional Services & More", label: "Professional Services & More", childCategories: [
      { "key": "equine_assisted_therapy", "label": "Equine-Assisted Therapy" },
      { "key": "equine_consultant", "label": "Equine Consultant" },
      { "key": "event_photographer", "label": "Event Photographer" },
      { "key": "fencing_installation", "label": "Fencing Installation" },
      { "key": "horse_transport", "label": "Horse Transport Services" },
      { "key": "mental_skills_coach", "label": "Mental Skills Coach" },
      { "key": "registry_org", "label": "Registry Organization" },
      { "key": "saddle_fitter", "label": "Saddle Fitter" },
      { "key": "sports_psychologist", "label": "Sports Psychologist" },
      { "key": "other", "label": "Other" }
    ]
  },
  {
    key: "Sales & Equipment", label: "Sales & Equipment", childCategories: [
      { "key": "accessories", "label": "Accessories" },
      { "key": "clothing_retailer", "label": "Clothing Retailer" },
      { "key": "equine_insurance_agent", "label": "Equine Insurance Agent" },
      { "key": "farm_equipment_sales", "label": "Farm Equipment Sales" },
      { "key": "fencing_sales", "label": "Fencing Sales" },
      { "key": "horse_equipment_retailer", "label": "Horse Equipment Retailer" },
      { "key": "horse_sales", "label": "Horse Sales" },
      { "key": "horse_show_ribbons_prizes", "label": "Horse Show Ribbons & Prizes" },
      { "key": "realtor_horse_properties", "label": "Realtor for Horse Properties" },
      { "key": "trailer_sales", "label": "Trailer Sales" },
      { "key": "truck_sales", "label": "Truck Sales" },
      { "key": "other", "label": "Other" }
    ]
  },
  {
    key: "Showing & Competition", label: "Showing & Competition", childCategories: [
      { "key": "course_designer", "label": "Course Designer" },
      { "key": "horse_show_judge", "label": "Horse Show Judge" },
      { "key": "horse_show_manager", "label": "Horse Show Manager" },
      { "key": "horse_show_office_staff", "label": "Horse Show Office Staff" },
      { "key": "horse_show_secretary", "label": "Horse Show Secretary" },
      { "key": "ring_steward", "label": "Ring Steward" },
      { "key": "scribe", "label": "Scribe" },
      { "key": "show_announcer", "label": "Show Announcer" },
      { "key": "show_clothing_designer", "label": "Show Clothing Designer" },
      { "key": "technical_delegate", "label": "Technical Delegate" },
      { "key": "other", "label": "Other" }
    ]
  },
  {
    key: "Tack & Equipment", label: "Tack & Equipment", childCategories: [
      { "key": "bedding_provider", "label": "Bedding Provider" },
      { "key": "blanket_washing", "label": "Blanket Washing" },
      { "key": "tack_accessories", "label": "Tack Accessories" },
      { "key": "tack_cleaning", "label": "Tack Cleaning" },
      { "key": "tack_repair", "label": "Tack Repair" },
      { "key": "tack_shop_owner", "label": "Tack Shop Owner" },
      { "key": "other", "label": "Other" }
    ]
  },
  {
    key: "Training & Lessons", label: "Training & Lessons", childCategories: [
      { "key": "colt_starter", "label": "Colt Starter" },
      { "key": "driving_instruction", "label": "Driving Instruction" },
      { "key": "horse_trainer", "label": "Horse Trainer" },
      { "key": "riding_lessons", "label": "Riding Lessons" },
      { "key": "show_coaching", "label": "Show Coaching" },
      { "key": "other", "label": "Other" }
    ]
  },
  {
    key: "Custom", label: "Custom",
    // childCategories: [
    //   { "key": "custom", "label": "Custom" }
    // ]
  },
];

export const listingFields = [
  // {
  //   "scope": "public",
  //   "label": "Gears",
  //   "key": "gears",
  //   "schemaType": "long",
  //   "numberConfig": {
  //     "minimum": 1,
  //     "maximum": 24
  //   },
  //   "filterConfig": {
  //     "indexForSearch": true,
  //     "group": "primary",
  //     "label": "Gears"
  //   }
  // }
  // {
  //   key: 'bikeType',
  //   scope: 'public',
  //   schemaType: 'enum',
  //   enumOptions: [
  //     { option: 'city-bikes', label: 'City bikes' },
  //     { option: 'electric-bikes', label: 'Electric bikes' },
  //     { option: 'mountain-bikes', label: 'Mountain bikes' },
  //     { option: 'childrens-bikes', label: "Children's bikes" },
  //   ],
  //   categoryConfig: {
  //     limitToCategoryIds: true,
  //     categoryIds: ['cats'],
  //   },
  //   filterConfig: {
  //     indexForSearch: true,
  //     filterType: 'SelectMultipleFilter', //'SelectSingleFilter',
  //     label: 'Bike type',
  //     group: 'primary',
  //   },
  //   showConfig: {
  //     label: 'Bike type',
  //     isDetail: true,
  //   },
  //   saveConfig: {
  //     label: 'Bike type',
  //     placeholderMessage: 'Select an option…',
  //     isRequired: true,
  //     requiredMessage: 'You need to select a bike type.',
  //   },
  // },
  // {
  //   key: 'tire',
  //   scope: 'public',
  //   schemaType: 'enum',
  //   enumOptions: [
  //     { option: '29', label: '29' },
  //     { option: '28', label: '28' },
  //     { option: '27', label: '27' },
  //     { option: '26', label: '26' },
  //     { option: '24', label: '24' },
  //     { option: '20', label: '20' },
  //     { option: '18', label: '18' },
  //   ],
  //   filterConfig: {
  //     indexForSearch: true,
  //     label: 'Tire size',
  //     group: 'secondary',
  //   },
  //   showConfig: {
  //     label: 'Tire size',
  //     isDetail: true,
  //   },
  //   saveConfig: {
  //     label: 'Tire size',
  //     placeholderMessage: 'Select an option…',
  //     isRequired: true,
  //     requiredMessage: 'You need to select a tire size.',
  //   },
  // },
  // {
  //   key: 'brand',
  //   scope: 'public',
  //   schemaType: 'enum',
  //   enumOptions: [
  //     { option: 'cube', label: 'Cube' },
  //     { option: 'diamant', label: 'Diamant' },
  //     { option: 'ghost', label: 'GHOST' },
  //     { option: 'giant', label: 'Giant' },
  //     { option: 'kalkhoff', label: 'Kalkhoff' },
  //     { option: 'kona', label: 'Kona' },
  //     { option: 'otler', label: 'Otler' },
  //     { option: 'vermont', label: 'Vermont' },
  //   ],
  //   filterConfig: {
  //     indexForSearch: true,
  //     label: 'Brand',
  //     group: 'secondary',
  //   },
  //   showConfig: {
  //     label: 'Brand',
  //     isDetail: true,
  //   },
  //   saveConfig: {
  //     label: 'Brand',
  //     placeholderMessage: 'Select an option…',
  //     isRequired: true,
  //     requiredMessage: 'You need to select a brand.',
  //   },
  // },
  // {
  //   key: 'accessories',
  //   scope: 'public',
  //   schemaType: 'multi-enum',
  //   enumOptions: [
  //     { option: 'bell', label: 'Bell' },
  //     { option: 'lights', label: 'Lights' },
  //     { option: 'lock', label: 'Lock' },
  //     { option: 'mudguard', label: 'Mudguard' },
  //   ],
  //   filterConfig: {
  //     indexForSearch: true,
  //     label: 'Accessories',
  //     searchMode: 'has_all',
  //     group: 'secondary',
  //   },
  //   showConfig: {
  //     label: 'Accessories',
  //   },
  //   saveConfig: {
  //     label: 'Accessories',
  //     placeholderMessage: 'Select an option…',
  //     isRequired: false,
  //   },
  // },
  // // An example of how to use transaction type specific custom fields and private data.
  // {
  //   key: 'note',
  //   scope: 'public',
  //   schemaType: 'text',
  //   listingTypeConfig: {
  //     limitToListingTypeIds: true,
  //     listingTypeIds: ['product-selling'],
  //   },
  //   showConfig: {
  //     label: 'Extra notes',
  //   },
  //   saveConfig: {
  //     label: 'Extra notes',
  //     placeholderMessage: 'Some public extra note about this bike...',
  //   },
  // },
  // {
  //   key: 'privatenote',
  //   scope: 'private',
  //   schemaType: 'text',
  //   listingTypeConfig: {
  //     limitToListingTypeIds: true,
  //     listingTypeIds: ['daily-booking'],
  //   },
  //   saveConfig: {
  //     label: 'Private notes',
  //     placeholderMessage: 'Some private note about this bike...',
  //   },
  // },
];

///////////////////////////////////////////////////////////////////////
// Configurations related to listing types and transaction processes //
///////////////////////////////////////////////////////////////////////

// A presets of supported listing configurations
//
// Note 1: The listingTypes come from listingTypes asset nowadays by default.
//         To use this built-in configuration, you need to change the overwrite from configHelper.js
//         (E.g. use mergeDefaultTypesAndFieldsForDebugging func)
// Note 2: transaction type is part of listing type. It defines what transaction process and units
//         are used when transaction is created against a specific listing.

/**
 * Configuration options for listing experience:
 * - listingType:         Unique string. This will be saved to listing's public data on
 *                        EditListingWizard.
 * - label                Label for the listing type. Used as microcopy for options to select
 *                        listing type in EditListingWizard.
 * - transactionType      Set of configurations how this listing type will behave when transaction is
 *                        created.
 *   - process              Transaction process.
 *                          The process must match one of the processes that this client app can handle
 *                          (check src/util/transactions/transaction.js) and the process must also exists in correct
 *                          marketplace environment.
 *   - alias                Valid alias for the aforementioned process. This will be saved to listing's
 *                          public data as transctionProcessAlias and transaction is initiated with this.
 *   - unitType             Unit type is mainly used as pricing unit. This will be saved to
 *                          transaction's protected data.
 *                          Recommendation: don't use same unit types in completely different processes
 *                          ('item' sold should not be priced the same as 'item' booked).
 * - stockType            This is relevant only to listings using default-purchase process.
 *                        If set to 'oneItem', stock management is not showed and the listing is
 *                        considered unique (stock = 1).
 *                        Possible values: 'oneItem' and 'multipleItems'.
 *                        Default: 'multipleItems'.
 * - defaultListingFields These are tied to transaction processes. Different processes have different flags.
 *                        E.g. default-inquiry can toggle price and location to true/false value to indicate,
 *                        whether price (or location) tab should be shown. If defaultListingFields.price is not
 *                        explicitly set to _false_, price will be shown.
 *                        If the location or pickup is not used, listing won't be returned with location search.
 *                        Use keyword search as main search type if location is not enforced.
 *                        The payoutDetails flag allows provider to bypass setting of payout details.
 *                        Note: customers can't order listings, if provider has not set payout details! Monitor
 *                        providers who have not set payout details and contact them to ensure that they add the details.
 */

export const listingTypes = [
  {
    listingType: 'daily-booking',
    label: 'Daily booking',
    transactionType: {
      process: 'default-booking',
      alias: 'default-booking/release-1',
      unitType: 'day',
    },
    defaultListingFields: {
      location: true,
      payoutDetails: true,
    },
  },
  // // Here are some examples for other listingTypes
  // // TODO: SearchPage does not work well if both booking and product selling are used at the same time
  // {
  //   listingType: 'nightly-booking',
  //   label: 'Nightly booking',
  //   transactionType: {
  //     process: 'default-booking',
  //     alias: 'default-booking/release-1',
  //     unitType: 'night',
  //   },
  // },
  // {
  //   listingType: 'hourly-booking',
  //   label: 'Hourly booking',
  //   transactionType: {
  //     process: 'default-booking',
  //     alias: 'default-booking/release-1',
  //     unitType: 'hour',
  //   },
  // },
  // {
  //   listingType: 'product-selling',
  //   label: 'Sell bicycles',
  //   transactionType: {
  //     process: 'default-purchase',
  //     alias: 'default-purchase/release-1',
  //     unitType: 'item',
  //   },
  //   stockType: 'multipleItems',
  //   defaultListingFields: {
  //     shipping: true,
  //     pickup: true,
  //     payoutDetails: true,
  //   },
  // },
  // {
  //   listingType: 'inquiry',
  //   label: 'Inquiry',
  //   transactionType: {
  //     process: 'default-inquiry',
  //     alias: 'default-inquiry/release-1',
  //     unitType: 'inquiry',
  //   },
  //   defaultListingFields: {
  //     price: false,
  //     location: true,
  //   },
  // },
];

// SearchPage can enforce listing query to only those listings with valid listingType
// However, it only works if you have set 'enum' type search schema for the public data fields
//   - listingType
//
//  Similar setup could be expanded to 2 other extended data fields:
//   - transactionProcessAlias
//   - unitType
//
// Read More:
// https://www.sharetribe.com/docs/how-to/manage-search-schemas-with-flex-cli/#adding-listing-search-schemas
export const enforceValidListingType = false;

export const associatedDisciplinesNotApplicable = "notApplicable";

export const associatedDisciplinesParent = [
  { value: 'English', label: 'English', id: 1 },
  { value: 'Western', label: 'Western', id: 2 },
  { value: 'Gaited Riding', label: 'Gaited Riding', id: 3 },
  { value: 'Pleasure Riding', label: 'Pleasure Riding', id: 4 },
  { value: 'Hunting', label: 'Hunting', id: 5 },
  { value: 'notApplicable', label: 'Not Applicable', id: 6 }
];

export const associatedDisciplinesChild = [
  { value: 'Reigning', label: 'Reigning', parentId: 2 },
  { value: 'Cutting', label: 'Cutting', parentId: 2 },
  { value: 'Barrel Racing', label: 'Barrel Racing', parentId: 2 },
  { value: 'Rodeo', label: 'Rodeo', parentId: 2 },
  { value: 'Trail Riding (Western Style)', label: 'Trail Riding (Western Style)', parentId: 2 },
  { value: 'Other', label: 'Other', parentId: 2, "isOther": true },
];


export const AllDynamicFieldsName = [
  {
    "key": "type",
    "label": "Type"
  },
  {
    "key": "brand",
    "label": "Brand"
  },
  {
    "key": "size",
    "label": "Size"
  },
  {
    "key": "size__in__inches",
    "label": "Size In Inches"
  },
  {
    "key": "blanket__size",
    "label": "Blanket Size"
  },
  {
    "key": "numeric__size",
    "label": "Numeric Size"
  },
  {
    "key": "chest__size",
    "label": "Chest Size"
  },
  {
    "key": "seat__size",
    "label": "Seat Size"
  },
  {
    "key": "waist__size",
    "label": "Waist Size"
  },
  {
    "key": "waist__in__inches",
    "label": "Waist In Inches"
  },
  {
    "key": "waist",
    "label": "Waist"
  },
  {
    "key": "color",
    "label": "Color"
  },
  {
    "key": "weight",
    "label": "Weight"
  },
  {
    "key": "blanket__weight",
    "label": "Blanket Weight"
  },
  {
    "key": "material",
    "label": "Material"
  },
  {
    "key": "length",
    "label": "Length"
  },
  {
    "key": "attachment",
    "label": "Attachment"
  },
  {
    "key": "form",
    "label": "Form"
  },
  {
    "key": "style",
    "label": "Style"
  },
  {
    "key": "thickness",
    "label": "Thickness"
  },
  {
    "key": "discipline",
    "label": "Discipline"
  },
  {
    "key": "english",
    "label": "English"
  },
  {
    "key": "western",
    "label": "Western"
  },
  {
    "key": "gender",
    "label": "Gender"
  },
  {
    "key": "stall__type",
    "label": "Stall Type"
  },
  {
    "key": "living__quarters",
    "label": "Living Quarters"
  }
];

export const productCondition = [
  { key: "New", label: "New" },
  { key: "Used", label: "Used" },
];

export const hasOfferDiscounts = [
  { key: "Price is firm", label: "Price is firm" },
  { key: "Yes: for 75% +", label: "Yes: for 75% +" },
  { key: "Yes: for 50% +", label: "Yes: for 50% +" },
  { key: "Yes: for all", label: "Yes: for all" },
];
// Medium Box | Dimensions Displayed Here
// Large Box | Dimensions Displayed Here
// Custom Box"

export const shipping_dimensions_types = [
  { key: 'Bubble Mailer 6" x 7"', label: 'Bubble Mailer 6" x 7"' },
  { key: 'Bubble Mailer 8" x 11"', label: 'Bubble Mailer 8" x 11"' },
  { key: 'Bubble Mailer 10" x 12"', label: 'Bubble Mailer 10" x 12"' },
  { key: 'Bubble Mailer 12" x 18"', label: 'Bubble Mailer 12" x 18"' },
  { key: 'Small Square Box 6" x 6" x 6"', label: 'Small Square Box 6" x 6" x 6"' },
  { key: 'Small Rectangle Box 12" x 3" x 17"', label: 'Small Rectangle Box 12" x 3" x 17"' },
  { key: 'Medium Square Box 11" x 11" x 11"', label: 'Medium Square Box 11" x 11" x 11"' },
  { key: 'Medium Rectangle Box 12" x 9" x 6"', label: 'Medium Rectangle Box 12" x 9" x 6"' },
  { key: 'Large Square Box 20" x 20" x 20"', label: 'Large Square Box 20" x 20" x 20"' },
  { key: 'Large Rectangle Box 18" x 13" x 11"', label: 'Large Rectangle Box 18" x 13" x 11"' },
  { key: "Custom Box", label: "Custom Box" },
];


const shopifyListingSchema = {
  publicData: {
    source: 'shopify', // tells shopify listing
    shopifyStatus: 'CONNECTED', // connection shopify status of listing
    isDeleted: false, // tells listing is deleted
    listingType: 'sell-used-products', // default listing type
    offers_discount: 'Price is firm',
    transactionProcessAlias: 'default-purchase/release-1',
    unitType: 'item',
    condition: 'Used',
    inventoryType: 'single',
    shopifyProductQuantity: 0
  }
}

const sharetribeListingSchema = {
  publicData: {
    source: 'sharetribe', // tells sharetribe listing
    isDeleted: false, // tells listing is deleted
  }
}
